import '@iotize/tap/service/impl/target';

import { Tap } from '@iotize/tap';
import { GPIOMode } from '@iotize/tap/service/impl/gpio';

import { debug } from './debug';
import { CONFIGURE_MASK, DIRECT_ACCESS_GPIO_FORMAT } from './definitions';
import { GPIO_MAPPING } from './gpio-mapping';
import {
  encodeMultiAddressInfo,
  encodeMultiIndexData,
} from './tap-gpio-converter';

export class GPIOManager {
  private _cache: Record<number, GPIOPin> = {};

  constructor(public readonly tap: Tap) {}

  getPinByIndex(index: number) {
    if (!(index in this._cache)) {
      this._cache[index] = new GPIOPin(this.tap, index);
    }
    return this._cache[index];
  }

  /**
   * TODO min api version ?
   * @param options
   */
  async readValues(
    indexs?: number[],
  ): Promise<{ index: number; value: boolean }[]> {
    return this._readMuliple<boolean>(
      indexs || GPIO_MAPPING.map((pin) => pin.index),
      false,
    );
  }

  /**
   * TODO min api version ?
   * @param options
   */
  async readConfig(
    indexs?: number[],
  ): Promise<{ index: number; value: GPIOMode }[]> {
    return this._readMuliple<GPIOMode>(
      indexs || GPIO_MAPPING.map((pin) => pin.index),
      true,
    );
  }

  private async _readMuliple<T extends number | boolean>(
    indexs: number[],
    config: boolean,
  ): Promise<{ index: number; value: T }[]> {
    const memoryInfo = encodeMultiAddressInfo(indexs, config);
    const results = (
      await this.tap.service.target.readAddress(memoryInfo)
    ).body();
    const formwattedResult = Array.from(results).map((v, index) => {
      return {
        index: memoryInfo.address + index,
        value: (config ? v : !!v) as T,
      };
    });
    return formwattedResult;
  }

  async writeValues(
    indexValues: { index: number; value: boolean }[] | boolean,
  ): Promise<void> {
    if (typeof indexValues === 'boolean') {
      indexValues = GPIO_MAPPING.map((pin) => ({
        index: pin.index,
        value: indexValues as boolean,
      }));
    }
    const indexs = indexValues.map((info) => info.index);
    const memoryInfo = encodeMultiAddressInfo(indexs);
    const encodedData = encodeMultiIndexData(indexValues);
    (
      await this.tap.service.target.writeAddress({
        data: encodedData,
        info: memoryInfo,
      })
    ).successful();
  }

  /**
   * TODO min api version ?
   * @param options
   */
  async writeConfig(
    indexValues: { index: number; value: GPIOMode }[] | GPIOMode,
  ) {
    if (typeof indexValues === 'number') {
      indexValues = GPIO_MAPPING.map((pin) => ({
        index: pin.index,
        value: indexValues as GPIOMode,
      }));
    }
    const indexs = indexValues.map((info) => info.index);
    const memoryInfo = encodeMultiAddressInfo(indexs, true);
    const encodedData = encodeMultiIndexData(indexValues);
    (
      await this.tap.service.target.writeAddress({
        data: encodedData,
        info: memoryInfo,
      })
    ).successful();
  }
}

export function pinIndexToConfigureAddress(index: number) {
  return (CONFIGURE_MASK | index) >>> 0;
}

export class GPIOPin {
  constructor(
    public readonly tap: Tap,
    public readonly index: number,
  ) {}

  async readConfig(): Promise<GPIOMode> {
    debug(`Read config for pin=${this.index}...`);
    const body = (
      await this.tap.service.target.readAddress({
        address: pinIndexToConfigureAddress(this.index),
        format: DIRECT_ACCESS_GPIO_FORMAT,
        length: 1,
      })
    ).body();
    // TODO check body.length >= 1
    return body[0] as GPIOMode;
  }

  async writeConfig(options: { mode: GPIOMode }) {
    if (!(options.mode in GPIOMode)) {
      throw new Error(`Unknown mode GPIO mode ${options.mode}`);
    }
    debug(`writeConfig on pin=${this.index}`, GPIOMode[options.mode]);
    (
      await this.tap.service.target.writeAddress({
        data: Uint8Array.from([options.mode]),
        info: {
          address: pinIndexToConfigureAddress(this.index),
          format: DIRECT_ACCESS_GPIO_FORMAT,
          length: 1,
        },
      })
    ).successful();
  }

  async readValue(): Promise<boolean> {
    const rawBody = (
      await this.tap.service.target.readAddress({
        address: this.index,
        format: DIRECT_ACCESS_GPIO_FORMAT,
        length: 1,
      })
    ).body();
    if (rawBody.length !== 1) {
      throw new Error(
        `Unexpected Tap response for read Address. Should be 1 byte but received ${rawBody.length} bytes`,
      );
    }
    const value = !!rawBody[0];
    debug(`Read value for pin=${this.index} ==> ${value}`);
    return value;
  }

  async writeValue(value: boolean): Promise<void> {
    debug(`Write value on pin=${this.index}; ${value}`);
    return (
      await this.tap.service.target.writeAddress({
        data: value ? Uint8Array.from([1]) : Uint8Array.from([0]),
        info: {
          address: this.index,
          format: DIRECT_ACCESS_GPIO_FORMAT,
          length: 1,
        },
      })
    ).body();
  }
}
